import { useEffect, useState } from "react";

// NYSE/NASDAQ regular hours: 09:30–16:00 ET, Mon–Fri, minus holidays.
// Early-close days end at 13:00 ET. The dashboard targets US equities;
// the holiday/early-close lists are hard-coded through 2027 — extend
// when the next year's calendar is published.
const HOLIDAYS = new Set<string>([
  "2026-01-01", "2026-01-19", "2026-02-16", "2026-04-03",
  "2026-05-25", "2026-06-19", "2026-07-03", "2026-09-07",
  "2026-11-26", "2026-12-25",
  "2027-01-01", "2027-01-18", "2027-02-15", "2027-03-26",
  "2027-05-31", "2027-06-18", "2027-07-05", "2027-09-06",
  "2027-11-25", "2027-12-24",
]);
const EARLY_CLOSE = new Set<string>([
  "2026-11-27", "2026-12-24",
  "2027-11-26",
]);

type ETParts = {
  year: number; month: number; day: number;
  hour: number; minute: number; weekday: number; // 0=Sun..6=Sat
  ymd: string;
};

function partsInET(d: Date): ETParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
    weekday: "short",
  });
  const m = Object.fromEntries(
    fmt.formatToParts(d).map((p) => [p.type, p.value]),
  ) as Record<string, string>;
  const wdMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const hour = m.hour === "24" ? 0 : parseInt(m.hour);
  return {
    year: parseInt(m.year),
    month: parseInt(m.month),
    day: parseInt(m.day),
    hour,
    minute: parseInt(m.minute),
    weekday: wdMap[m.weekday],
    ymd: `${m.year}-${m.month}-${m.day}`,
  };
}

// Convert an ET wall-clock (year/month/day/hour/minute) to a real Date.
// Works by adding the ET<->UTC offset measured at that approximate instant.
// Safe outside DST-transition hours (2am ET), which the market never opens at.
function etWallClockToDate(y: number, mo: number, d: number, h: number, mi: number): Date {
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  const probe = new Date(naive);
  const p = partsInET(probe);
  const etInstant = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  return new Date(naive + (naive - etInstant));
}

function isTradingDay(p: { weekday: number; ymd: string }): boolean {
  if (p.weekday === 0 || p.weekday === 6) return false;
  return !HOLIDAYS.has(p.ymd);
}

function closeHourFor(ymd: string): number {
  return EARLY_CLOSE.has(ymd) ? 13 : 16;
}

type Status =
  | { state: "open"; closeAt: Date }
  | { state: "closed"; openAt: Date };

function computeStatus(now: Date): Status {
  const et = partsInET(now);
  const minutesNow = et.hour * 60 + et.minute;
  if (isTradingDay(et)) {
    const closeMin = closeHourFor(et.ymd) * 60;
    if (minutesNow >= 9 * 60 + 30 && minutesNow < closeMin) {
      const closeH = closeHourFor(et.ymd);
      const closeAt = etWallClockToDate(et.year, et.month, et.day, closeH, 0);
      return { state: "open", closeAt };
    }
  }
  // Walk forward day-by-day until we hit a trading day, then anchor to 09:30 ET.
  // If today is a trading day and we're pre-open, the 0-day step catches it.
  for (let i = 0; i < 10; i++) {
    const probe = etWallClockToDate(et.year, et.month, et.day, 12, 0);
    probe.setUTCDate(probe.getUTCDate() + i);
    const pp = partsInET(probe);
    if (!isTradingDay(pp)) continue;
    if (i === 0 && et.hour * 60 + et.minute >= 9 * 60 + 30) continue;
    const openAt = etWallClockToDate(pp.year, pp.month, pp.day, 9, 30);
    return { state: "closed", openAt };
  }
  // Should be unreachable — 10 days always contains a trading day.
  return { state: "closed", openAt: now };
}

function formatLocal(d: Date): string {
  const fmt = new Intl.DateTimeFormat(undefined, {
    weekday: "short", hour: "numeric", minute: "2-digit",
    timeZoneName: "short",
  });
  return fmt.format(d);
}

function formatLocalTime(d: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(d);
}

function formatETTime(d: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(d);
}

function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export function MarketStatusBanner() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const status = computeStatus(now);
  const etNow = formatETTime(now);
  if (status.state === "open") {
    return (
      <div className="market-status market-status-open">
        <span className="market-status-dot" />
        <strong>Market open</strong>
        <span className="market-status-detail">
          closes {formatLocalTime(status.closeAt)}
        </span>
        <span className="market-status-et">{etNow}</span>
      </div>
    );
  }
  const opensLabel = sameLocalDay(now, status.openAt)
    ? `today at ${formatLocalTime(status.openAt)}`
    : formatLocal(status.openAt);
  return (
    <div className="market-status market-status-closed">
      <span className="market-status-dot" />
      <strong>Market closed</strong>
      <span className="market-status-detail">opens {opensLabel}</span>
      <span className="market-status-et">{etNow}</span>
    </div>
  );
}
